<template>
  <div class="flex flex-col max-w-[1100px] mx-auto p-10">
    <div>
      <p class="text-gray-500"> {{ $t("shared.account.manage_companies.explanation_text") }} </p>
    </div>

		<VcInput
     v-model ="search"
      class="w-full py-4 focus:outline-none focus:ring-2 focus:ring-sky-400"
      :placeholder="$t('shared.account.manage_companies.search_placeholder')"
    >
      <template #append>
        <button v-if="search" type="button" class="flex h-full items-center px-3" @click="reset">
          <VcIcon name="delete-2" size="xs" class="text-primary" />
        </button>

        <VcButton
          :aria-label="$t('shared.layout.search_bar.search_button')"
          icon="search"
          @click="goToSearchResultsPage"
        />
      </template>
    </VcInput>

    <div class="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5">
			<div
				v-for="company in filteredCompanies"
				:key="company.id"
				class="flex flex-col border border-gray-200 rounded-xl p-5 bg-white transition hover:-translate-y-[2px] hover:shadow-lg hover:cursor-pointer"
				:class="company.id === activeCompanyId ? 'border-2 border-success-500' : ''"
				@click="company.id === activeCompanyId && redirectCompanyMembers()"
			>

				<div class="flex items-center gap-3">
					<div class="w-9 h-9 rounded-lg bg-primary-400 flex items-center justify-center font-black text-secondary text-xl font-[650]">
						{{ company.name.charAt(0) }}
					</div>

					<h3 class="font-semibold">
						{{ company.name }}
					</h3>
				</div>

				<div class="my-4">
					<div
						v-if="addressesByOrganization[company.id]"
						class="flex items-center gap-1 text-gray-500 text-sm"
					>
            <VcIcon class="text-neutral ml-2 mb-1" name="location-marker" size="sm" />
						<span class="text-neutral">
							{{ addressesByOrganization[company.id].city }},
							{{ addressesByOrganization[company.id].countryName }}
						</span>
					</div>
				</div>

				<div class="flex justify-end mt-auto">
					<span
						v-if="company.id === activeCompanyId"
						class="bg-success-100 text-success-700 px-2 py-1 rounded text-sm font-bold"
					>
						{{ $t("shared.account.manage_companies.active_company") }}
					</span>

					<VcButton
						v-else
						@click.stop="switchCompany(company)"
						variant="secondary"
						class="px-3 py-2 !rounded cursor-pointer"
						size="sm"
					>
						{{ $t("common.buttons.switch") }}
					</VcButton>
				</div>
			</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { getOrganizationAddresses } from "@/core/api/graphql/organization";

const router = useRouter();
const route = useRoute();

const emit = defineEmits(["switch-company"]);

interface Company {
  id: string;
  name: string;
};

interface Address {
  city?: string
  countryName?: string
};

const props = defineProps<{
  companies: Company[];
  activeCompanyId: string;
}>();


const search = ref("");
const newActiveCompanyId = ref("");
const addressesByOrganization = ref<Record<string, Address>>({})

const filteredCompanies = computed(() => {
  const term = search.value.toLowerCase().trim()

  if (!term) {
    return props.companies ?? []
  }

  return (props.companies ?? []).filter((company: Company) =>
    company.name?.toLowerCase().includes(term)
  )
})

function switchCompany(company: Company) {
  newActiveCompanyId.value = company.id;
  emit("switch-company", company);
};

async function loadCompaniesWithAddresses(organizations: any[]) {
  const map: Record<string, any> = {};

  await Promise.all(
    organizations.map(async (org) => {
      const { items = [] } = await getOrganizationAddresses(org.id, {
        first: 1,
      });

      map[org.id] = items[0] ?? null;
    })
  );

  addressesByOrganization.value = map;
};

function redirectCompanyMembers () {
	router.push("/company/members");
};

function reset() {
  search.value = ""
};

function goToSearchResultsPage() {
  console.log("search:", search.value)
};

onMounted(async () => {
  await loadCompaniesWithAddresses(props.companies);
});
</script>